import { useEffect, useState } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { IconPlus, IconTrash, IconCheck } from '@tabler/icons-react';
import {
  ConfirmModal,
  FormSelectField,
  FormSubmitButton,
  FormCancelButton,
  ErrorAlert,
  FormField,
  WarningAlert,
  withModalMount,
} from '@petseo/shared';
import { Button } from '@petseo/shared/components/layout/common';
import { FormModal } from '@/components/FormModal';
import { scheduleSchema, type ScheduleFormData } from '@/schemas/schedule.schema';
import { useCreateSchedule, useUpdateSchedule, useDeleteSchedule, useEmployeeName, useScheduleBusinessHoursValidation } from '../../hooks';
import { buildDefaultScheduleFormData } from '../../selectors';
import { useServices, useBusinessHours } from '@/features/service-vendors/hooks/api';
import { getDayName, formatTimeDisplay, DAYS_ORDER, toApiDateFormat } from '@/utils/datetime';
import { getTimeOptions } from '@/utils/mappers';
import {
  type ServiceVendorId,
  type EmployeeId,
  type ScheduleDto,
  type DayOfWeek,
  createEmployeeScheduleId,
} from '@/services/api/petseo.client.wrapper';
import { ServiceCheckboxList } from '../schedules/ServiceCheckboxList';
import { pl } from '@/messages/pl';

interface ScheduleFormProps {
  serviceVendorId: ServiceVendorId;
  employeeId: EmployeeId;
  schedule?: ScheduleDto;
  defaultDate?: string;
  isOpen: boolean;
  onClose: () => void;
}

const DEFAULT_BLOCKS = [{ startTime: '09:00', endTime: '17:00' }];
const DAY_CARD_MIN_WIDTH_PX = 320;

function mapScheduleToFormData(schedule: ScheduleDto): ScheduleFormData {
  return {
    validFrom: schedule.validFrom ? new Date(schedule.validFrom).toISOString().slice(0, 10) : '',
    validTo: schedule.validTo ? new Date(schedule.validTo).toISOString().slice(0, 10) : '',
    days: (schedule.days ?? []).map((day) => ({
      dayOfWeek: day.dayOfWeek ?? 0,
      blocks:
        day.timeRanges && day.timeRanges.length > 0
          ? day.timeRanges.map((b) => ({ startTime: formatTimeDisplay(b.startTime), endTime: formatTimeDisplay(b.endTime) }))
          : [...DEFAULT_BLOCKS],
    })),
    serviceIds: (schedule.serviceIds ?? []).map(String),
  };
}

function ScheduleFormContent({ serviceVendorId, employeeId, schedule, defaultDate, isOpen, onClose }: ScheduleFormProps) {
  const createSchedule = useCreateSchedule();
  const updateSchedule = useUpdateSchedule();
  const deleteSchedule = useDeleteSchedule();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: vendorServices } = useServices(serviceVendorId);
  const { data: employeeName } = useEmployeeName(serviceVendorId, employeeId);
  const { data: businessHours } = useBusinessHours(serviceVendorId);

  const isEditing = !!schedule;

  const mutationError = isEditing ? updateSchedule.error : createSchedule.error;

  const timeOptions = getTimeOptions(15, 0, 24);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ScheduleFormData>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: schedule ? mapScheduleToFormData(schedule) : buildDefaultScheduleFormData(defaultDate),
  });

  const { fields: dayFields, append: appendDay, remove: removeDay } = useFieldArray({ control, name: 'days' });

  const days = useWatch({ control, name: 'days' });
  const serviceIds = useWatch({ control, name: 'serviceIds' });

  useEffect(() => {
    if (isOpen) {
      reset(schedule ? mapScheduleToFormData(schedule) : buildDefaultScheduleFormData(defaultDate));
    }
  }, [isOpen, schedule, defaultDate, reset]);

  const selectedDays = new Set(days.map((d) => d.dayOfWeek));

  const { hasNoBusinessHoursDefined, hasScheduleOutsideBusinessHours } = useScheduleBusinessHoursValidation(days, businessHours);

  const handleToggleDay = (dayOfWeek: DayOfWeek) => {
    if (selectedDays.has(dayOfWeek)) {
      const index = days.findIndex((d) => d.dayOfWeek === dayOfWeek);
      if (index !== -1) removeDay(index);
    } else {
      appendDay({ dayOfWeek, blocks: [...DEFAULT_BLOCKS] });
    }
  };

  const handleAddBlock = (dayIndex: number) => {
    const currentBlocks = days[dayIndex]?.blocks ?? [];
    setValue(`days.${dayIndex}.blocks`, [...currentBlocks, { startTime: '09:00', endTime: '17:00' }]);
  };

  const handleRemoveBlock = (dayIndex: number, blockIndex: number) => {
    const currentBlocks = days[dayIndex]?.blocks ?? [];
    setValue(
      `days.${dayIndex}.blocks`,
      currentBlocks.filter((_, i) => i !== blockIndex)
    );
  };

  const keepVisibleServiceIds = (ids: string[]) => {
    if (!vendorServices) return ids;
    const visibleIds = new Set(vendorServices.map((service) => String(service.id)));
    return ids.filter((id) => visibleIds.has(id));
  };

  const onSubmit = async (data: ScheduleFormData) => {
    const payload = {
      validFrom: toApiDateFormat(new Date(data.validFrom)) as unknown as Date,
      validTo: toApiDateFormat(new Date(data.validTo)) as unknown as Date,
      days: data.days.map((d) => ({
        dayOfWeek: d.dayOfWeek,
        blocks: d.blocks.map((b) => ({ startTime: b.startTime, endTime: b.endTime })),
      })),
      serviceIds: keepVisibleServiceIds(data.serviceIds),
    };

    if (isEditing) {
      await updateSchedule.mutateAsync({
        serviceVendorId,
        serviceVendorEmployeeId: employeeId,
        scheduleId: createEmployeeScheduleId(schedule!.id!),
        data: payload,
      });
    } else {
      await createSchedule.mutateAsync({
        serviceVendorId,
        serviceVendorEmployeeId: employeeId,
        data: payload,
      });
    }
    onClose();
  };

  const handleDelete = async () => {
    if (!schedule?.id) return;
    await deleteSchedule.mutateAsync({
      serviceVendorId,
      serviceVendorEmployeeId: employeeId,
      scheduleId: createEmployeeScheduleId(schedule.id),
    });
    setShowDeleteConfirm(false);
    onClose();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? pl.scheduleForm.editTitle : `${pl.scheduleForm.createTitle}${employeeName ? ` - ${employeeName}` : ''}`}
      size="xl">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="modal-body">
          {mutationError && <ErrorAlert apiError={mutationError} />}

          {hasNoBusinessHoursDefined && (
            <WarningAlert title={pl.scheduleForm.noBusinessHoursTitle} message={pl.scheduleForm.noBusinessHoursMessage} className="mb-3" />
          )}

          {hasScheduleOutsideBusinessHours && (
            <WarningAlert title={pl.scheduleForm.outsideBusinessHoursTitle} message={pl.scheduleForm.outsideBusinessHoursMessage} className="mb-3" />
          )}

          <div className="row mb-3">
            <div className="col-6">
              <FormField control={control} name="validFrom" label={pl.scheduleForm.validFromLabel} type="date" error={errors.validFrom} />
            </div>
            <div className="col-6">
              <FormField control={control} name="validTo" label={pl.scheduleForm.validToLabel} type="date" error={errors.validTo} />
            </div>
          </div>

          <ServiceCheckboxList
            services={vendorServices ?? []}
            selectedIds={serviceIds}
            onChange={(ids) => setValue('serviceIds', ids, { shouldValidate: true })}
            error={errors.serviceIds && typeof errors.serviceIds.message === 'string' ? errors.serviceIds.message : undefined}
          />

          <label className="form-label fw-bold">{pl.scheduleForm.daysLabel}</label>

          {dayFields.length === 0 && errors.days && typeof errors.days.message === 'string' && <ErrorAlert errorMessage={errors.days.message} />}

          <div className="d-flex gap-2 flex-wrap mb-3">
            {DAYS_ORDER.map((day) => {
              const isSelected = selectedDays.has(day);
              return (
                <Button key={day} type="button" variant={isSelected ? 'primary' : 'outline'} size="sm" onClick={() => handleToggleDay(day)}>
                  {isSelected ? <IconCheck size={14} className="me-1" /> : <IconPlus size={14} className="me-1" />}
                  {getDayName(day)}
                </Button>
              );
            })}
          </div>

          <div
            data-testid="schedule-day-cards"
            style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${DAY_CARD_MIN_WIDTH_PX}px, 1fr))`, gap: '0.5rem' }}>
            {dayFields
              .map((dayField, dayIndex) => ({ dayField, dayIndex, dayOfWeek: days[dayIndex]?.dayOfWeek ?? 0 }))
              .sort((a, b) => DAYS_ORDER.indexOf(a.dayOfWeek as DayOfWeek) - DAYS_ORDER.indexOf(b.dayOfWeek as DayOfWeek))
              .map(({ dayField, dayIndex, dayOfWeek }) => {
                return (
                  <div key={dayField.id}>
                    <div className="card">
                      <div className="card-header d-flex align-items-center justify-content-between gap-2 py-2">
                        <strong className="text-truncate">{getDayName(dayOfWeek as DayOfWeek)}</strong>
                        <Button type="button" variant="danger" size="sm" className="flex-shrink-0" onClick={() => removeDay(dayIndex)}>
                          <IconTrash size={16} />
                        </Button>
                      </div>
                      <div className="card-body py-2">
                        {days[dayIndex]?.blocks.map((_, blockIndex) => {
                          const hasMultipleBlocks = (days[dayIndex]?.blocks?.length ?? 0) > 1;
                          return (
                            <div key={blockIndex} className="row g-2 mb-2 align-items-end">
                              <div className={hasMultipleBlocks ? 'col' : 'col-6'}>
                                <FormSelectField
                                  control={control}
                                  name={`days.${dayIndex}.blocks.${blockIndex}.startTime`}
                                  label={blockIndex === 0 ? pl.scheduleForm.blockStartLabel : ''}
                                  wrapperClassName=""
                                  options={timeOptions}
                                  error={errors.days?.[dayIndex]?.blocks?.[blockIndex]?.startTime}
                                />
                              </div>
                              <div className={hasMultipleBlocks ? 'col' : 'col-6'}>
                                <FormSelectField
                                  control={control}
                                  name={`days.${dayIndex}.blocks.${blockIndex}.endTime`}
                                  label={blockIndex === 0 ? pl.scheduleForm.blockEndLabel : ''}
                                  wrapperClassName=""
                                  options={timeOptions}
                                  error={errors.days?.[dayIndex]?.blocks?.[blockIndex]?.endTime}
                                />
                              </div>
                              {hasMultipleBlocks && (
                                <div className="col-auto">
                                  <Button type="button" variant="danger" size="sm" onClick={() => handleRemoveBlock(dayIndex, blockIndex)}>
                                    <IconTrash size={14} />
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}

                        <Button type="button" variant="secondary" size="sm" onClick={() => handleAddBlock(dayIndex)}>
                          <IconPlus size={14} className="me-1" />
                          {pl.scheduleForm.addBlock}
                        </Button>

                        {errors.days?.[dayIndex]?.blocks && typeof errors.days[dayIndex].blocks.message === 'string' && (
                          <ErrorAlert errorMessage={errors.days[dayIndex].blocks.message} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          {isEditing ? (
            <Button type="button" variant="danger" onClick={() => setShowDeleteConfirm(true)} disabled={deleteSchedule.isPending}>
              <IconTrash size={16} className="me-1" />
              {pl.scheduleForm.delete}
            </Button>
          ) : (
            <span />
          )}
          <div className="d-flex gap-2">
            <FormCancelButton onClick={onClose} />
            <FormSubmitButton isLoading={isSubmitting} disabled={isSubmitting} className="btn btn-primary">
              {pl.scheduleForm.submit}
            </FormSubmitButton>
          </div>
        </div>
      </form>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={pl.scheduleForm.deleteConfirmTitle}
        message={pl.scheduleForm.deleteConfirmMessage}
        confirmText={pl.scheduleForm.delete}
        cancelText={pl.scheduleForm.deleteConfirmCancel}
        isLoading={deleteSchedule.isPending}
      />
    </FormModal>
  );
}

export const ScheduleForm = withModalMount(ScheduleFormContent);
